import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import FacialAccess from './FacialAccess';
import { API_URL, formatRut, formatTelefono } from '../constants';

const PLANES = [
  { id: 'open',   name: 'Open Gym',    price: '$29.990/mes' },
  { id: 'openpt', name: 'Open + PT',   price: '$59.990/mes' },
  { id: 'lunmar', name: 'Lun/Mar',     price: '$19.990/mes' },
  { id: 'diario', name: 'Pase Diario', price: '$3.990'      },
];

export default function Totem({ alumnos, onNuevoAlumno, onAcceso }) {
  const [modo, setModo] = useState('idle');
  const [rut, setRut] = useState('');
  const [alumnoEncontrado, setAlumnoEncontrado] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [planSel, setPlanSel] = useState('open');
  const [form, setForm] = useState({ rut: '', nombre: '', apellido: '', telefono: '', email: '', fecha_nacimiento: '' });
  const [ingresos, setIngresos] = useState(0);
  const [renovacionesHoy, setRenovacionesHoy] = useState([]);
  const [nuevosHoy, setNuevosHoy] = useState([]);
  const [errorRut, setErrorRut] = useState('');
  const rutRef = useRef(null);
  const rutRenovRef = useRef(null);

  useEffect(() => {
    if (modo === 'idle' && rutRef.current) rutRef.current.focus();
    if (modo === 'renovar_rut' && rutRenovRef.current) rutRenovRef.current.focus();
    if (modo !== 'idle' && modo !== 'renovar_rut') setErrorRut('');
  }, [modo]);

  const handleRutChange = (e) => setRut(formatRut(e.target.value));

  // Flujo normal: verificar acceso
  const checkRut = async () => {
    if (!rut.trim()) return;
    setBuscando(true);
    setErrorRut('');
    try {
      const res = await axios.get(`${API_URL}/alumnos/rut/${rut.replace(/\./g, '')}`);
      if (res.data) {
        setAlumnoEncontrado(res.data);
        if (res.data.revisado === false) {
          setModo('negado');
        } else if (res.data.estado === 'activo' && res.data.dias_vigentes > 0) {
          onAcceso && onAcceso(res.data.id);
          setIngresos(i => i + 1);
          setModo('aprobado');
          setTimeout(() => resetTotem(), 5000);
        } else {
          setModo('negado');
        }
      } else {
        setAlumnoEncontrado(null);
        setModo('negado');
      }
    } catch {
      setAlumnoEncontrado(null);
      setModo('negado');
    } finally {
      setBuscando(false);
    }
  };

  // Flujo renovación: buscar alumno (activo o vencido)
  const checkRutRenovar = async () => {
    if (!rut.trim()) return;
    setBuscando(true);
    setErrorRut('');
    try {
      const res = await axios.get(`${API_URL}/alumnos/rut/${rut.replace(/\./g, '')}`);
      if (res.data) {
        setAlumnoEncontrado(res.data);
        setPlanSel('open');
        setModo('renovar_plan');
      } else {
        setErrorRut('RUT no encontrado. Verifica el número o regístrate como alumno nuevo.');
      }
    } catch {
      // Si la API falla, buscar en la lista local como fallback
      const alumnoLocal = alumnos.find(a => a.rut?.replace(/\./g, '') === rut.replace(/\./g, ''));
      if (alumnoLocal) {
        setAlumnoEncontrado(alumnoLocal);
        setPlanSel('open');
        setModo('renovar_plan');
      } else {
        setErrorRut('RUT no encontrado. Verifica el número o regístrate como alumno nuevo.');
      }
    } finally {
      setBuscando(false);
    }
  };

  // Confirmar renovación
  const handleRenovar = async () => {
    if (!alumnoEncontrado) return;
    const planNombre = PLANES.find(p => p.id === planSel)?.name || 'Open Gym';
    try {
      await axios.put(`${API_URL}/alumnos/${alumnoEncontrado.id}`, {
        plan: planNombre,
        estado: 'activo',
      });
    } catch {
      // Si la API no tiene el endpoint, igual mostramos éxito en demo
    }
    setRenovacionesHoy(prev => [...prev, {
      nombre: `${alumnoEncontrado.nombre} ${alumnoEncontrado.apellido}`,
      rut: alumnoEncontrado.rut,
      plan: planNombre,
    }]);
    setModo('renovado');
    setTimeout(() => resetTotem(), 6000);
  };

  const handleRegistrar = async (e) => {
    e.preventDefault();
    const planNombre = PLANES.find(p => p.id === planSel)?.name || 'Open Gym Mensual';
    await onNuevoAlumno({ ...form, plan: planNombre });
    setNuevosHoy(n => [...n, { nombre: form.nombre + ' ' + form.apellido, rut: form.rut, plan: planNombre }]);
    resetTotem();
  };

  const resetTotem = () => {
    setModo('idle');
    setRut('');
    setAlumnoEncontrado(null);
    setErrorRut('');
    setForm({ rut: '', nombre: '', apellido: '', telefono: '', email: '', fecha_nacimiento: '' });
  };

  const morosos = alumnos.filter(a => a.estado !== 'activo' || a.dias_vigentes <= 0).length;

  return (
    <div style={{ minHeight: 'calc(100vh - 57px)', display: 'flex', alignItems: 'stretch' }}>

      {/* Panel izquierdo — tótem */}
      <div className="totem-screen">
        <div className="totem-header">
          <div className="totem-logo">∞ INFINITY <span>CENTER</span></div>
          <div className="totem-sub">Sistema de Acceso</div>
        </div>

        {/* IDLE */}
        {modo === 'idle' && (
          <div className="totem-idle-wrap">
            <div className="totem-prompt">Bienvenido<br /><span>¿Qué deseas hacer?</span></div>

            <div className="totem-grid">
              <button className="totem-btn" onClick={() => setModo('registrar')}>
                <span className="tb-icon">➕</span>
                <span className="tb-label">Soy Nuevo</span>
                <span className="tb-sub">Registrarme ahora</span>
              </button>
              <button className="totem-btn" onClick={() => { setRut(''); setModo('renovar_rut'); }}>
                <span className="tb-icon">🔄</span>
                <span className="tb-label">Renovación</span>
                <span className="tb-sub">Extender membresía</span>
              </button>
              <button className="totem-btn" onClick={() => rutRef.current?.focus()}>
                <span className="tb-icon">🏋️</span>
                <span className="tb-label">Ingresar</span>
                <span className="tb-sub">Verificar acceso</span>
              </button>
              <button className="totem-btn" onClick={() => rutRef.current?.focus()}>
                <span className="tb-icon">📋</span>
                <span className="tb-label">Pase Diario</span>
                <span className="tb-sub">Compra en 30 seg</span>
              </button>
            </div>

            <button className="totem-btn-facial" onClick={() => setModo('facial')}>
              <span style={{ fontSize: 22 }}>🤖</span>
              <span>
                <span className="facial-btn-label">Acceso Facial</span>
                <span className="facial-btn-sub">Reconocimiento con IA</span>
              </span>
            </button>

            <div className="totem-divider">O ingresa tu RUT directamente</div>

            <div className="rut-box">
              <div className="rut-label">RUT del alumno</div>
              <div className="rut-input-wrap">
                <input
                  ref={rutRef}
                  type="text"
                  className="rut-input"
                  placeholder="12.345.678-9"
                  maxLength={12}
                  value={rut}
                  onChange={handleRutChange}
                  onKeyDown={e => e.key === 'Enter' && checkRut()}
                />
                <button className="rut-btn" onClick={checkRut} disabled={buscando}>→</button>
              </div>
            </div>
          </div>
        )}

        {/* RENOVAR — ingresar RUT */}
        {modo === 'renovar_rut' && (
          <div className="totem-idle-wrap">
            <button className="back-btn" onClick={resetTotem} style={{ alignSelf: 'flex-start' }}>← Volver</button>
            <div className="totem-prompt" style={{ fontSize: 28 }}>Renovar<br /><span>Membresía</span></div>
            <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', marginTop: -10 }}>
              Ingresa tu RUT para buscar tu cuenta
            </div>

            <div className="rut-box">
              <div className="rut-label">RUT del alumno</div>
              <div className="rut-input-wrap">
                <input
                  ref={rutRenovRef}
                  type="text"
                  className="rut-input"
                  placeholder="12.345.678-9"
                  maxLength={12}
                  value={rut}
                  onChange={handleRutChange}
                  onKeyDown={e => e.key === 'Enter' && checkRutRenovar()}
                />
                <button className="rut-btn" onClick={checkRutRenovar} disabled={buscando}>→</button>
              </div>
            </div>

            {errorRut && (
              <div style={{ background: 'rgba(255,59,59,.1)', border: '1px solid var(--red)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: 'var(--red)', textAlign: 'center', width: '100%' }}>
                {errorRut}
              </div>
            )}

            {buscando && (
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>Buscando...</div>
            )}
          </div>
        )}

        {/* RENOVAR — seleccionar plan */}
        {modo === 'renovar_plan' && alumnoEncontrado && (
          <div className="totem-register-wrap">
            <button className="back-btn" onClick={() => setModo('renovar_rut')}>← Volver</button>
            <div className="reg-title">Renovar Membresía</div>

            {/* Info del alumno */}
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 4 }}>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
                {alumnoEncontrado.nombre} {alumnoEncontrado.apellido}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
                {alumnoEncontrado.rut}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                  background: alumnoEncontrado.dias_vigentes > 0 ? 'rgba(0,229,160,.15)' : 'rgba(255,59,59,.15)',
                  color: alumnoEncontrado.dias_vigentes > 0 ? 'var(--green)' : 'var(--red)',
                  border: `1px solid ${alumnoEncontrado.dias_vigentes > 0 ? 'var(--green)' : 'var(--red)'}`,
                }}>
                  {alumnoEncontrado.dias_vigentes > 0 ? `${alumnoEncontrado.dias_vigentes} días restantes` : 'Membresía vencida'}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Plan actual: {alumnoEncontrado.plan}</div>
              </div>
            </div>

            <div className="form-section" style={{ marginTop: 12 }}>Elige el nuevo plan</div>
            <div className="plan-cards-grid">
              {PLANES.map(p => (
                <div key={p.id} className={`plan-card ${planSel === p.id ? 'selected' : ''}`} onClick={() => setPlanSel(p.id)}>
                  <div className="pc-name">{p.name}</div>
                  <div className="pc-price">{p.price}</div>
                </div>
              ))}
            </div>

            <button className="form-submit" style={{ marginTop: 12 }} onClick={handleRenovar}>
              🔄 Confirmar Renovación
            </button>
          </div>
        )}

        {/* RENOVADO — éxito */}
        {modo === 'renovado' && alumnoEncontrado && (
          <div className="totem-result" style={{ color: 'var(--green)' }}>
            <div className="access-check">✓</div>
            <div className="access-title">¡RENOVADO!</div>
            <div className="access-name">{alumnoEncontrado.nombre} {alumnoEncontrado.apellido}</div>
            <div className="access-plan">{PLANES.find(p => p.id === planSel)?.name}</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', marginTop: 8 }}>
              Tu membresía ha sido renovada correctamente.
            </div>
            <div className="access-progress"><div className="access-progress-bar" /></div>
            <button className="back-btn" onClick={resetTotem} style={{ marginTop: 8 }}>← Volver al inicio</button>
          </div>
        )}

        {/* APROBADO */}
        {modo === 'aprobado' && alumnoEncontrado && (
          <div className="totem-result" style={{ color: 'var(--green)' }}>
            <div className="access-check">✓</div>
            <div className="access-title">ACCESO APROBADO</div>
            <div className="access-name">{alumnoEncontrado.nombre} {alumnoEncontrado.apellido}</div>
            <div className="access-plan">{alumnoEncontrado.plan}</div>
            <div className="access-days">
              <div className="access-days-num">{alumnoEncontrado.dias_vigentes}</div>
              <div className="access-days-label">Días Vigentes</div>
              <div className="access-vence">Vence el {alumnoEncontrado.fecha_vencimiento}</div>
            </div>
            <div className="access-racha">
              <div className="racha-icon">🔥</div>
              <div>
                <div className="racha-text">Racha activa</div>
                <div className="racha-val">Bienvenido</div>
              </div>
            </div>
            <div className="access-progress"><div className="access-progress-bar" /></div>
            <button className="back-btn" onClick={resetTotem} style={{ marginTop: 8 }}>← Volver</button>
          </div>
        )}

        {/* DENEGADO */}
        {modo === 'negado' && (
          <div className="totem-result">
            <div className="deny-icon">✗</div>
            <div className="deny-title">ACCESO DENEGADO</div>
            <div className="deny-msg">
              {alumnoEncontrado?.revisado === false
                ? 'Tu registro está pendiente de aprobación por el staff. Acércate a recepción.'
                : alumnoEncontrado
                  ? <>Membresía vencida. <button className="link-btn" onClick={() => { setRut(''); setModo('renovar_rut'); }}>Renovar aquí →</button></>
                  : 'RUT no encontrado en el sistema. Si eres nuevo, selecciona "Soy Nuevo".'}
            </div>
            <button className="back-btn" onClick={resetTotem} style={{ marginTop: 16 }}>← Volver</button>
          </div>
        )}

        {/* FACIAL */}
        {modo === 'facial' && (
          <FacialAccess
            alumnos={alumnos}
            onVolver={resetTotem}
            onAprobado={(alumno) => {
              setAlumnoEncontrado(alumno);
              onAcceso && onAcceso(alumno.id);
              setIngresos(i => i + 1);
              setTimeout(() => resetTotem(), 7000);
            }}
          />
        )}

        {/* REGISTRAR */}
        {modo === 'registrar' && (
          <div className="totem-register-wrap">
            <button className="back-btn" onClick={resetTotem}>← Volver</button>
            <div className="reg-title">Registro de Alumno Nuevo</div>
            <form onSubmit={handleRegistrar} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="form-field">
                <div className="form-label">RUT *</div>
                <input className="form-input" placeholder="12.345.678-9" value={form.rut} onChange={e => setForm({ ...form, rut: e.target.value })} required />
              </div>
              <div className="form-row-2">
                <div className="form-field">
                  <div className="form-label">Nombre *</div>
                  <input className="form-input" placeholder="Juan" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required />
                </div>
                <div className="form-field">
                  <div className="form-label">Apellido *</div>
                  <input className="form-input" placeholder="Pérez" value={form.apellido} onChange={e => setForm({ ...form, apellido: e.target.value })} required />
                </div>
              </div>
              <div className="form-field">
                <div className="form-label">Teléfono *</div>
                <input className="form-input" placeholder="+56 9 1234 5678" value={form.telefono} onChange={e => setForm({ ...form, telefono: formatTelefono(e.target.value) })} />
              </div>
              <div className="form-field">
                <div className="form-label">Email</div>
                <input className="form-input" placeholder="correo@ejemplo.com" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-section">Elige tu Plan</div>
              <div className="plan-cards-grid">
                {PLANES.map(p => (
                  <div key={p.id} className={`plan-card ${planSel === p.id ? 'selected' : ''}`} onClick={() => setPlanSel(p.id)}>
                    <div className="pc-name">{p.name}</div>
                    <div className="pc-price">{p.price}</div>
                  </div>
                ))}
              </div>
              <button type="submit" className="form-submit">✓ Completar Registro</button>
            </form>
          </div>
        )}
      </div>

      {/* Panel derecho — admin */}
      <div className="totem-admin-right">
        <div className="admin-title-right">Panel de Administración</div>

        <div className="sim-section">
          <div className="sim-label">Estadísticas del día</div>
          <div className="stats-grid-3">
            <div className="stat-card">
              <div className="stat-num green">{ingresos}</div>
              <div className="stat-label">Ingresos hoy</div>
            </div>
            <div className="stat-card">
              <div className="stat-num orange">{nuevosHoy.length}</div>
              <div className="stat-label">Registros nuevos</div>
            </div>
            <div className="stat-card">
              <div className="stat-num red">{morosos}</div>
              <div className="stat-label">Morosos</div>
            </div>
          </div>
        </div>

        <div className="sim-section">
          <div className="sim-label">Simular acceso — haz clic en un alumno</div>
          <div className="alumnos-sim-list">
            {alumnos.filter(a => a.revisado !== false).map(a => {
              const estado = a.estado === 'activo' && a.dias_vigentes > 5 ? 'ok' : a.dias_vigentes > 0 ? 'warn' : 'bad';
              const colores = { ok: 'var(--green)', warn: 'var(--cyan)', bad: 'var(--red)' };
              const textos = { ok: 'Al día', warn: 'Por vencer', bad: 'Vencido' };
              return (
                <div key={a.id} className="alumno-card-sim" onClick={() => { setRut(a.rut); setModo('idle'); setTimeout(() => { setRut(a.rut); checkRutDirect(a); }, 100); }}>
                  <div className="alumno-info">
                    <div className="alumno-nombre-sim">{a.nombre} {a.apellido}</div>
                    <div className="alumno-rut-sim">{a.rut} — {a.plan}</div>
                  </div>
                  <div className="alumno-status-sim">
                    <div className={`status-dot ${estado}`} />
                    <span className="status-text-sim" style={{ color: colores[estado] }}>{textos[estado]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {renovacionesHoy.length > 0 && (
          <div className="sim-section">
            <div className="sim-label">Renovaciones hoy</div>
            {renovacionesHoy.map((r, i) => (
              <div key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.nombre}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.rut} — {r.plan}</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 600 }}>🔄 Renovado</span>
              </div>
            ))}
          </div>
        )}

        <div className="sim-section">
          <div className="sim-label">Alumnos registrados hoy</div>
          {nuevosHoy.length === 0
            ? <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>Aún no hay registros nuevos en esta sesión.</div>
            : nuevosHoy.map((r, i) => (
              <div key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.nombre}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{r.rut} — {r.plan}</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600 }}>✓ Registrado</span>
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );

  function checkRutDirect(alumno) {
    setAlumnoEncontrado(alumno);
    if (alumno.revisado === false) {
      setModo('negado');
    } else if (alumno.estado === 'activo' && alumno.dias_vigentes > 0) {
      onAcceso && onAcceso(alumno.id);
      setIngresos(i => i + 1);
      setModo('aprobado');
      setTimeout(() => resetTotem(), 5000);
    } else {
      setModo('negado');
    }
  }
}
